import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ConsentCategory,
  ConsentContextType,
  ConsentPreferences,
} from '../types/contexts';

// Re-export for backward compatibility
export type { ConsentPreferences };

const defaultPrefs: ConsentPreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

const STORAGE_KEY = 'cgminis.cookieConsent.v1';

/** Sync read of stored preferences so CartProvider gets correct cartId on first load (avoids race) */
const getStoredPreferences = (): ConsentPreferences => {
  try {
    if (typeof window === 'undefined') return defaultPrefs;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...defaultPrefs, ...parsed, necessary: true };
    }
  } catch {
    // ignore
  }
  return defaultPrefs;
};

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preferences, setPreferences] =
    useState<ConsentPreferences>(getStoredPreferences);
  const [hasSetPreferences, setHasSetPreferences] = useState<boolean>(() =>
    typeof window !== 'undefined' ? !!localStorage.getItem(STORAGE_KEY) : false
  );

  // Keep stored prefs in sync (e.g. when user accepts/rejects from another tab)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setPreferences({ ...defaultPrefs, ...parsed, necessary: true });
        setHasSetPreferences(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const persist = useCallback((prefs: ConsentPreferences) => {
    setPreferences(prefs);
    setHasSetPreferences(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // storage might be unavailable
    }
  }, []);

  const updatePreferences = useCallback(
    (prefs: Partial<ConsentPreferences>) => {
      persist({ ...preferences, ...prefs, necessary: true });
    },
    [persist, preferences]
  );

  const acceptAll = useCallback(() => {
    persist({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  }, [persist]);

  const isEnabled = useCallback(
    (category: ConsentCategory) => {
      if (category === 'necessary') return true;
      return !!preferences[category];
    },
    [preferences]
  );

  const value = useMemo(
    () => ({
      hasSetPreferences,
      preferences,
      updatePreferences,
      acceptAll,
      rejectNonEssential,
      isEnabled,
    }),
    [
      acceptAll,
      hasSetPreferences,
      isEnabled,
      preferences,
      rejectNonEssential,
      updatePreferences,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextType => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
  return ctx;
};
